import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  type AppVersionUploadData,
  appVersionApi,
} from '@/domains/system-management/apis/app-version';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const LIST_KEY = ['app-version', 'list'];

// Same shape as useLanguageManagement: list query + create / update / delete
// mutations that toast, invalidate the list, then hand control back to the page.
export const useAppVersion = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const toastError = (error: unknown) => {
    const axiosError = error as AxiosError<{ message: string }>;
    const message = axiosError.response?.data?.message || axiosError.message;
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    new NoticeMessage(t(message));
  };

  // Release history (Preference Management table)
  const useAppVersionList = () => {
    return useQuery({
      queryKey: LIST_KEY,
      queryFn: () => appVersionApi.getList(),
    });
  };

  // Publish a release: APK + version -> SeaweedFS + new app_version row
  const useUploadAppVersion = (onCreateSuccess?: (data: AppVersionUploadData | null) => void) => {
    return useMutation({
      mutationFn: (data: FormData) => appVersionApi.upload(data),
      onSuccess: (response) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: LIST_KEY });
            if (onCreateSuccess) onCreateSuccess(response.data);
          },
        });
      },
      onError: toastError,
    });
  };

  // Edit a release: version / notes, optionally replace the APK
  const useUpdateAppVersion = (onUpdateSuccess?: (data: AppVersionUploadData | null) => void) => {
    return useMutation({
      mutationFn: ({ appId, data }: { appId: number; data: FormData }) =>
        appVersionApi.update(appId, data),
      onSuccess: (response) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: LIST_KEY });
            if (onUpdateSuccess) onUpdateSuccess(response.data);
          },
        });
      },
      onError: toastError,
    });
  };

  // Remove a release and its APK
  const useDeleteAppVersion = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (appId: number) => appVersionApi.delete(appId),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: LIST_KEY });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: toastError,
    });
  };

  return {
    useAppVersionList,
    useUploadAppVersion,
    useUpdateAppVersion,
    useDeleteAppVersion,
  };
};
